import { useState, useEffect, useCallback } from "react";
import { Grid, Paper } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { CheckCircle, Cancel, Block } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import NavigationHeader from "components/NavigationHeader";
import { StripedDataGrid } from "components/StripedDataGrid";
import { FloatMenuCrud } from "components/FloatMenuCrud";
import { FilterColumn } from "components/StripedDataGrid/FilterColumn";
import { TitleTable } from "components/TitleTable";
import ConfirmDeleteModal from "components/ConfirmDeleteModal";
import { DashboardStackCustom, StackCustom } from "components/StackCustom/styles";
import { useNavigation } from "contexts/notificationsContext";
import { debounce } from "lodash";
import { TIME_DEBOUNCE } from "hooks/usePagination";
import {
  getProjetistas,
  getDadosPaginacao,
  deleteProjetista,
  postAtivarDesativarProjetista,
} from "services/modules/projetistas";
import { IProjetista } from "shared/interfaces/projetista";
import { ModalProjetista } from "./modalCadastroProjetista";

export const Projetistas = () => {
  const { openNotification } = useNavigation();
  const [projetistas, setProjetistas] = useState<IProjetista[]>([]);
  const [totalItemsPagina, setTotalItens] = useState<number>(0);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [isLoadingDelete, setIsLoadingDelete] = useState(false);

  const [isOpenModal, setIsOpenModal] = useState<{
    isOpen: boolean;
    data?: IProjetista;
  }>({ isOpen: false });

  const [modalDelete, setModalDelete] = useState<{
    isOpen: boolean;
    id?: number;
  }>({ isOpen: false });

  const [modalDisabled, setModalDisabled] = useState<{
    isOpen: boolean;
    id?: number;
    ativar?: boolean;
  }>({ isOpen: false });

  const [filterColumnsDebounced, setFilterColumnsDebounced] = useState<{
    column?: "nome";
    value?: string;
  }>();

  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    sortBy: "",
    direction: "asc",
    search: "",
  });

  const debouncedFilterColumn = useCallback(
    debounce((column: "nome", value: string) => {
      setFilterColumnsDebounced({ column, value });
    }, TIME_DEBOUNCE),
    []
  );

  const columns: GridColDef[] = [
    {
      field: "nome",
      headerName: "Nome do Projetista",
      flex: 1,
      renderHeader: () => (
        <FilterColumn
          label="Nome do Projetista"
          name="nome"
          onChange={(event) => {
            debouncedFilterColumn("nome", event.target.value);
          }}
        />
      ),
    },
    {
      field: "superReservado",
      headerName: "Super reservado",
      sortable: false,
      flex: 0.15,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        return params.row.superReservado ? (
          <CheckCircle color="success" />
        ) : (
          <Cancel color="disabled" />
        );
      },
    },
    {
      field: "ativo",
      headerName: "Ativo",
      sortable: false,
      flex: 0.15,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        return params.row.ativo ? (
          <CheckCircle color="success" />
        ) : (
          <Cancel color="disabled" />
        );
      },
    },
    {
      field: "options",
      sortable: false,
      headerName: "Ações",
      align: "center",
      headerAlign: "center",
      flex: 0.15,
      renderCell: (params: any) => {
        return (
          <FloatMenuCrud
            onEdit={() => setIsOpenModal({ isOpen: true, data: params.row })}
            showDuplicateOption={false}
            deleteButtonText="Excluir"
            onDelete={() => setModalDelete({ isOpen: true, id: params.row.idProjetista })}
            otherOptions={[
              {
                icon: params.row.ativo ? <Cancel /> : <CheckCircle />,
                label: params.row.ativo ? "DESATIVAR" : "ATIVAR",
                onClickButton: () =>
                  setModalDisabled({
                    isOpen: true,
                    id: params.row.idProjetista,
                    ativar: !params.row.ativo,
                  }),
                color: params.row.ativo ? "error" : "success",
              },
            ]}
          />
        );
      },
    },
  ];

  const getProjetistasData = async (params?: any) => {
    setIsLoadingData(true);
    try {
      const result = await getProjetistas(params);
      const totalItemsPagina = await getDadosPaginacao(params);
      
      setProjetistas(result);
      setTotalItens(totalItemsPagina);
    } catch (error: any) {
      openNotification({
        message: "Não foi possível carregar os Projetistas",
        type: "error",
      });
    } finally {
      setIsLoadingData(false);
    }
  };

  const handleDeleteProjetista = async (id?: number) => {
    if (!id) return;
    setIsLoadingDelete(true);
    try {
      const result = await deleteProjetista(id);
      
      if (result) {
        openNotification({
          message: "Projetista excluído com sucesso!",
          type: "success",
        });
        await getProjetistasData(pagination);
      }
    } catch (error: any) {
      openNotification({
        message: error?.message || "Erro ao excluir projetista",
        type: "error",
      });
    } finally {
      setIsLoadingDelete(false);
      setModalDelete({ isOpen: false });
    }
  };

  const handleAtivarDesativar = async (id?: number, ativar?: boolean) => {
    if (!id || ativar === undefined) return;
    try {
      const result = await postAtivarDesativarProjetista(id, ativar);
      
      if (result) {
        openNotification({
          message: `Projetista ${ativar ? "ativado" : "desativado"} com sucesso!`,
          type: "success",
        });
        await getProjetistasData(pagination);
      }
    } catch (error: any) {
      openNotification({
        message: error?.message || "Erro ao alterar status do projetista",
        type: "error",
      });
    } finally {
      setModalDisabled({ isOpen: false });
    }
  };

  const handlePagination = (updates: Partial<typeof pagination>) => {
    setPagination((prev) => ({ ...prev, ...updates }));
  };

  useEffect(() => {
    if (filterColumnsDebounced) {
      const paginacao = {
        page: 1,
        pageSize: 10,
        sortBy: "",
        direction: pagination?.direction || "asc",
        search: filterColumnsDebounced.value || "",
      };
      handlePagination(paginacao);
      getProjetistasData(paginacao);
    } else {
      getProjetistasData(pagination);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterColumnsDebounced]);

  useEffect(() => {
    getProjetistasData(pagination);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page, pagination.pageSize]);

  return (
    <DashboardStackCustom>
      <NavigationHeader
        title="CADASTRO DE PROJETISTAS"
        label="Projetistas"
        buttonLabel="Cadastrar"
        buttonOnClick={() => setIsOpenModal({ isOpen: true })}
        hasBackButton={false}
      />
      <Grid container display="flex" alignItems="center">
        <StackCustom>
          <Grid container mb={4}>
            <Grid item xs={12} sm={8} md={12}>
              <TitleTable title="Listagem de projetistas do MOF" />
            </Grid>
          </Grid>
          <Paper>
            <StripedDataGrid
              rows={projetistas}
              columns={columns}
              getRowId={(row: IProjetista) => row.idProjetista}
              page={pagination.page - 1}
              pageSize={pagination.pageSize}
              rowCount={totalItemsPagina}
              loading={isLoadingData || isLoadingDelete}
              onPageChange={(newPage) => handlePagination({ page: newPage + 1 })}
              onPageSizeChange={(newPageSize) => {
                handlePagination({
                  pageSize: newPageSize,
                  page: 1,
                });
              }}
              checkboxSelection={false}
              rowHeight={70}
            />
          </Paper>
        </StackCustom>
      </Grid>

      <ModalProjetista
        isOpen={isOpenModal.isOpen}
        onClose={() => setIsOpenModal({ isOpen: false })}
        refreshData={() => getProjetistasData(pagination)}
        dataProjetista={isOpenModal.data}
      />

      <ConfirmDeleteModal
        title="Tem certeza que deseja excluir o Projetista?"
        description="Essa operação não pode ser desfeita."
        open={modalDelete.isOpen}
        onClose={() => setModalDelete({ isOpen: false })}
        onConfirm={() => handleDeleteProjetista(modalDelete.id)}
      />

      <ConfirmDeleteModal
        title={`Tem certeza que deseja ${modalDisabled.ativar ? "ativar" : "desativar"} o Projetista?`}
        description={
          modalDisabled.ativar
            ? "O projetista voltará a ficar disponível no sistema."
            : "As tipologias associadas ficarão indisponíveis no CEM Desktop e ocultas no CEM MOBI."
        }
        open={modalDisabled.isOpen}
        onClose={() => setModalDisabled({ isOpen: false })}
        onConfirm={() => handleAtivarDesativar(modalDisabled.id, modalDisabled.ativar)}
        customButtonConfirm={
          <LoadingButton
            variant="contained"
            size="medium"
            color={modalDisabled.ativar ? "success" : "error"}
            endIcon={modalDisabled.ativar ? <CheckCircle /> : <Block />}
            onClick={() => handleAtivarDesativar(modalDisabled.id, modalDisabled.ativar)}
          >
            {modalDisabled.ativar ? "ATIVAR" : "DESATIVAR"}
          </LoadingButton>
        }
      />
    </DashboardStackCustom>
  );
};

